import { FormLabel, RequiredIndicator } from "assets/js/Styles";
import FormGenerator from "components/customs/form-generator/input";
import { ADD_ACTION, VIEW_ACTION } from "constants/actions";
import React from "react";
import { Col, Row } from "reactstrap";
import styled from "styled-components";
import { canEditRoles } from "utility/constants";
import CustomSelect from "components/customs/dropdown";
import { addProjectDropdownStyles } from "constants/style";
import CustomCheckbox from "components/customs/checkbox";
import { Check } from "react-feather";
import CustomDropzone from "views/projects/add/Dropzone";
import { Toast } from "utility/helpers";
import RecipientTable from "./Table";

const FormSection = styled.div`
  margin-top: 44px;

  .react-datepicker-wrapper {
    width: 100%;
  }

  @media (min-width: 576px) and (max-width: 767px) {
    padding-left: 1rem;
    padding-right: 1rem;

    .date-section {
      .col-md-6:last-child {
        margin-top: 1rem;
      }
    }
  }

  .react-datepicker__time-container {
    width: 100px !important; /* Adjust width as needed */
  }

  .react-datepicker__time-box {
    width: 100% !important;
  }

  .react-datepicker__time-list-item {
    margin: 15px auto !important;
  }
`;

const SendWithOptions = styled.div`
  display: flex;
  gap: 2rem;
`;

const RowSection = styled(Row)`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormColRight = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    width: 50%;
  }

  .dropzone-container {
    width: auto;
    height: 65px;
    border: 1px dashed #a3a3a3;

    .upload-files {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
`;

const CharacterCountSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const EventFormSection = ({
  form,
  setForm,
  action,
  startEventDate,
  setEventStartDate,
  endEventDate,
  setEventEndDate,
  selectedStartTime,
  setSelectedStartTime,
  selectedEndTime,
  setSelectedEndTime,
  role,
  handleRegionOnChange,
  selectedRegion,
  getFilteredRegionOptions,
  selectedRecipients,
  setRecipients,
  recipients,
  isCustomSelected,
  isLegacyData,
  startRSVPDate,
  setRSVPStartDate,
  endRSVPDate,
  setRSVPEndDate,
  uploadedFiles,
  setUploadedFiles,
}) => {
  const handleDeleteFile = (file) => {
    const newUploadedFiles = uploadedFiles.filter(
      (files) => files.name !== file
    );
    setUploadedFiles(newUploadedFiles);
  };

  const handleUploadedFiles = (link) => {
    setUploadedFiles((prevUploadedFiles) => {
      const fileIndex = prevUploadedFiles.findIndex(
        (file) => file.name === link.name
      );

      if (fileIndex !== -1) {
        const updatedFiles = [...prevUploadedFiles];
        updatedFiles[fileIndex] = link;

        return updatedFiles;
      }

      if (prevUploadedFiles.length < 5) {
        return [...prevUploadedFiles, link];
      }

      Toast({
        type: 4,
        content: "Exceeded file upload limit of 5",
      });

      return prevUploadedFiles;
    });
  };

  return (
    <FormSection>
      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"Name"}
            {RequiredIndicator}
          </FormLabel>
          <p>{"Provide the name of the event"}</p>
        </Col>
        <FormColRight>
          <FormGenerator
            type="text"
            placeholder="Name"
            value={form.eventName}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const { value } = event.target;

              setForm({
                ...form,
                eventName: value,
              });
            }}
          />
        </FormColRight>
      </RowSection>

      <RowSection className="date-section">
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"Date"}
            {RequiredIndicator}
          </FormLabel>
          <p>{"Provide the date of the event"}</p>
        </Col>
        <FormColRight>
          <Row>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={startEventDate}
                placeholderText="Start Date"
                dateFormat="MMM-dd-yyyy"
                minDate={new Date()}
                onChange={(date) => {
                  setEventStartDate(date);
                }}
                readOnly={action === VIEW_ACTION}
              />
            </Col>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={endEventDate}
                placeholderText="End Date"
                dateFormat="MMM-dd-yyyy"
                minDate={new Date()}
                onChange={(date) => {
                  setEventEndDate(date);
                }}
                readOnly={action === VIEW_ACTION}
              />
            </Col>
          </Row>
        </FormColRight>
      </RowSection>

      <RowSection className="date-section">
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"Time"}
            {RequiredIndicator}
          </FormLabel>
          <p>{"Provide the time of the event"}</p>
        </Col>
        <FormColRight>
          <Row>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={selectedStartTime}
                onChange={(time) => setSelectedStartTime(time)}
                showTimeSelect
                showTimeSelectOnly
                placeholderText="Start Time"
                timeFormat="hh:mm aa"
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                readOnly={action === VIEW_ACTION}
              />
            </Col>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={selectedEndTime}
                onChange={(time) => setSelectedEndTime(time)}
                showTimeSelect
                showTimeSelectOnly
                placeholderText="End Time"
                timeFormat="hh:mm aa"
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                readOnly={action === VIEW_ACTION}
              />
            </Col>
          </Row>
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"Venue"}
            {RequiredIndicator}
          </FormLabel>
          <p>{"Provide the location of the event"}</p>
        </Col>
        <FormColRight>
          <FormGenerator
            type="text"
            placeholder="Venue"
            value={form.eventVenue}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const { value } = event.target;

              setForm({
                ...form,
                eventVenue: value,
              });
            }}
          />
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>{"Description"}</FormLabel>
          <p>{"Provide the description of the event"}</p>
        </Col>
        <FormColRight>
          <FormGenerator
            type="textarea"
            placeholder="Description"
            rows="5"
            value={form.eventDescription}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const { value } = event.target;

              setForm({
                ...form,
                eventDescription: value,
              });
            }}
          />
        </FormColRight>
      </RowSection>

      {canEditRoles.includes(role) && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Invitees By Region"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Provide the region of the invitees"}</p>
          </Col>
          <FormColRight>
            <CustomSelect
              placeholder="Region"
              options={getFilteredRegionOptions()}
              onChange={handleRegionOnChange}
              styles={addProjectDropdownStyles}
              value={selectedRegion}
              isDisabled={action !== ADD_ACTION}
              isMulti
            />
          </FormColRight>
        </RowSection>
      )}

      {canEditRoles.includes(role) && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Invitee"}
              {RequiredIndicator}
            </FormLabel>
          </Col>
          <FormColRight>
            <SendWithOptions>
              <CustomCheckbox
                checked={form.invitationFor === "both"}
                label="Both"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    invitationFor: "both",
                  });
                }}
              />

              <CustomCheckbox
                checked={form.invitationFor === "congOnly"}
                label="Cong"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    invitationFor: "congOnly",
                  });
                }}
              />

              <CustomCheckbox
                checked={form.invitationFor === "cosOnly"}
                label="COS"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    invitationFor: "cosOnly",
                  });
                }}
              />
            </SendWithOptions>
          </FormColRight>
        </RowSection>
      )}

      {canEditRoles.includes(role) && (
        <RecipientTable
          data={selectedRecipients}
          setRecipients={setRecipients}
          recipients={recipients}
          action={action}
          isCustomSelected={isCustomSelected}
          isLegacy={isLegacyData}
        />
      )}

      {canEditRoles.includes(role) && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Send SMS To"}</FormLabel>
          </Col>
          <FormColRight>
            <SendWithOptions>
              <CustomCheckbox
                checked={form.sendTo === "both"}
                label="Both"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    sendTo: form.sendTo === "both" ? "" : "both",
                  });
                }}
              />

              <CustomCheckbox
                checked={form.sendTo === "congOnly"}
                label="Cong"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    sendTo: form.sendTo === "congOnly" ? "" : "congOnly",
                  });
                }}
              />

              <CustomCheckbox
                checked={form.sendTo === "cosOnly"}
                label="COS"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={() => {
                  setForm({
                    ...form,
                    sendTo: form.sendTo === "cosOnly" ? "" : "cosOnly",
                  });
                }}
              />
            </SendWithOptions>
          </FormColRight>
        </RowSection>
      )}

      <RowSection className="date-section">
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"RSVP Date"}
            {RequiredIndicator}
          </FormLabel>
          <p>{"Provide the date of response"}</p>
        </Col>
        <FormColRight>
          <Row>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={startRSVPDate}
                placeholderText="Start Date"
                dateFormat="MMM-dd-yyyy"
                minDate={new Date()}
                onChange={(date) => {
                  setRSVPStartDate(date);
                }}
                readOnly={action === VIEW_ACTION}
              />
            </Col>
            <Col md="6">
              <FormGenerator
                type="datetime"
                selected={endRSVPDate}
                placeholderText="End Date"
                dateFormat="MMM-dd-yyyy"
                minDate={new Date()}
                onChange={(date) => {
                  setRSVPEndDate(date);
                }}
                readOnly={action === VIEW_ACTION}
              />
            </Col>
          </Row>
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>{"Message"}</FormLabel>
          <p>{"Write your message here"}</p>
        </Col>
        <FormColRight>
          <FormGenerator
            type="textarea"
            placeholder="Compose a message"
            rows="5"
            value={form.message}
            maxLength={600}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const { value } = event.target;

              if (value.length <= 600) {
                setForm({
                  ...form,
                  message: value,
                });
              }
            }}
          />
          <CharacterCountSection>
            {`Characters remaining: ${600 - form.message.length}`}
          </CharacterCountSection>
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>{"Supporting Documents"}</FormLabel>
          <p>{"Upload the supporting documents"}</p>
        </Col>
        <FormColRight>
          <CustomDropzone
            handleUploadedFiles={handleUploadedFiles}
            handleDeleteFile={handleDeleteFile}
            uploadedFiles={uploadedFiles}
            label="Upload Files"
            isDisabled={action === VIEW_ACTION}
          />
        </FormColRight>
      </RowSection>
    </FormSection>
  );
};

export default EventFormSection;
